import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchTabData, FetchTabDataRejection } from "./thunk";
import { setSelectedOrder } from "src/slices/Filters/reducer";

// Types

export interface TabPagination {
  totalRecords: number;
  totalPages:   number;
  currentPage:  number;
  perPage:      number;
}

/** One cache entry per exchangeId:tabId combination. */
export interface TabDataEntry {
  /** Signature of the params that produced `rows`. Empty string = never fetched. */
  fetchKey:      string;
  /** Filter-only signature (no page/sort) of the cached data — for staleness detection. */
  queryIdentity: string;
  rows:          Record<string, unknown>[];
  pagination:    TabPagination;
  isLoading:     boolean;
  errorMessage:  string | null;
  // View-state — persisted here (not in the hook) so it survives the tab's unmount.
  currentPage:   number;
  perPage:       number;
  sortColumn:    string;       // '' until the hook seeds the registry default
  sortDirection: "asc" | "desc";
  /** Metrics the user is permitted to see — from API response. Static across select/deselect. */
  permittedMetrics: string[];
  /** User's selected metrics — ordered array. Array order = column display order. */
  selectedMetrics:  string[];
}

export interface TabDataState {
  /** Keyed by `${exchangeId}:${tabId}`. */
  entries: Record<string, TabDataEntry>;
}

// Helpers

export function buildCacheKey(exchangeId: string | number, tabId: string): string {
  return `${exchangeId}:${tabId}`;
}

function createEmptyEntry(): TabDataEntry {
  return {
    fetchKey:      "",
    queryIdentity: "",
    rows:          [],
    pagination:    { totalRecords: 0, totalPages: 1, currentPage: 1, perPage: 10 },
    isLoading:     false,
    errorMessage:  null,
    currentPage:   1,
    perPage:       10,
    sortColumn:    "",
    sortDirection: "asc",
    permittedMetrics: [],
    selectedMetrics:  [],
  };
}

export const emptyTabDataEntry: TabDataEntry = createEmptyEntry();

// Initial state

const initialState: TabDataState = {
  entries: {},
};

// Slice

const tabDataSlice = createSlice({
  name: "tabData",
  initialState,
  reducers: {
    /** Merge page/sort/perPage into an entry (creating it from empty if absent). */
    setTabView(
      state,
      action: PayloadAction<{
        cacheKey:       string;
        currentPage?:   number;
        perPage?:       number;
        sortColumn?:    string;
        sortDirection?: "asc" | "desc";
      }>
    ) {
      const { cacheKey, currentPage, perPage, sortColumn, sortDirection } = action.payload;
      const entry = state.entries[cacheKey] ?? createEmptyEntry();
      if (currentPage   !== undefined) entry.currentPage   = currentPage;
      if (perPage       !== undefined) entry.perPage       = perPage;
      if (sortColumn    !== undefined) entry.sortColumn    = sortColumn;
      if (sortDirection !== undefined) entry.sortDirection = sortDirection;
      state.entries[cacheKey] = entry;
    },

    /**
     * Empty an entry's data — used when the selection becomes empty. Resets the page to
     * 1 (so a later re-selection starts fresh) but keeps perPage/sort user preferences.
     */
    clearTabData(state, action: PayloadAction<{ cacheKey: string }>) {
      const { cacheKey } = action.payload;
      const entry = state.entries[cacheKey] ?? createEmptyEntry();
      entry.fetchKey      = "";
      entry.queryIdentity = "";
      entry.rows          = [];
      entry.pagination    = { totalRecords: 0, totalPages: 1, currentPage: 1, perPage: entry.perPage };
      entry.isLoading     = false;
      entry.errorMessage  = null;
      entry.currentPage   = 1;
      state.entries[cacheKey] = entry;
    },

    /** Update the user's selected metrics (ordered) for a tab. */
    setTabSelectedMetrics(
      state,
      action: PayloadAction<{ cacheKey: string; metrics: string[] }>
    ) {
      const { cacheKey, metrics } = action.payload;
      const entry = state.entries[cacheKey] ?? createEmptyEntry();
      entry.selectedMetrics = metrics;
      state.entries[cacheKey] = entry;
    },

    /** Drop every cache entry — call when leaving the Reports page. */
    resetTabData() {
      return initialState;
    },
  },

  extraReducers: (builder) => {
    // When the selected order changes, every cached tab is stale → clear all.
    builder.addCase(setSelectedOrder, (state) => {
      state.entries = {};
    });

    builder
      .addCase(fetchTabData.pending, (state, action) => {
        const { cacheKey } = action.meta.arg;
        const existingEntry = state.entries[cacheKey] ?? createEmptyEntry();
        existingEntry.isLoading    = true;
        existingEntry.errorMessage = null;
        state.entries[cacheKey] = existingEntry;
      })
      .addCase(fetchTabData.fulfilled, (state, action) => {
        const { cacheKey, fetchKey, queryIdentity, rows, pagination, permittedMetrics } = action.payload;
        // Preserve view-state; only data fields are replaced.
        const existingEntry = state.entries[cacheKey] ?? createEmptyEntry();
        existingEntry.fetchKey      = fetchKey;
        existingEntry.queryIdentity = queryIdentity;
        existingEntry.rows          = rows;
        existingEntry.pagination    = pagination;
        existingEntry.isLoading     = false;
        existingEntry.errorMessage  = null;

        // Store permitted metrics from API. Seed selectedMetrics on first load only.
        // The backend returns the correct permitted_metrics based on user type and tab_access.
        if (permittedMetrics.length > 0) {
          existingEntry.permittedMetrics = permittedMetrics;
          if (existingEntry.selectedMetrics.length === 0) {
            existingEntry.selectedMetrics = [...permittedMetrics];
          }
        }

        state.entries[cacheKey] = existingEntry;
      })
      .addCase(fetchTabData.rejected, (state, action) => {
        const rejection = action.payload as FetchTabDataRejection | undefined;
        const cacheKey = rejection?.cacheKey ?? action.meta.arg.cacheKey;
        const existingEntry = state.entries[cacheKey] ?? createEmptyEntry();
        existingEntry.isLoading    = false;
        existingEntry.errorMessage = rejection?.message ?? "Failed to load tab data";
        state.entries[cacheKey] = existingEntry;
      });
  },
});

export const { setTabView, clearTabData, resetTabData, setTabSelectedMetrics } = tabDataSlice.actions;
export default tabDataSlice.reducer;
